import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Clock, FolderKanban, CheckCircle2, TrendingUp } from 'lucide-react';

interface TeamMemberCardProps {
  member: {
    id: number;
    name: string;
    email: string;
    avatar_url?: string;
    role: string;
    department?: string;
    status: string;
    hourly_rate?: number;
    stats?: {
      total_hours: number;
      projects_assigned: number;
      tasks_completed: number;
      utilization: number;
    };
  };
}

const roleColors: Record<string, string> = {
  admin: 'bg-purple-500/10 text-purple-500',
  project_manager: 'bg-blue-500/10 text-blue-500',
  developer: 'bg-green-500/10 text-green-500',
  designer: 'bg-pink-500/10 text-pink-500',
  qa: 'bg-orange-500/10 text-orange-500',
  content_writer: 'bg-yellow-500/10 text-yellow-500',
  client_success: 'bg-indigo-500/10 text-indigo-500',
};

const statusColors: Record<string, string> = {
  active: 'bg-green-500/10 text-green-500',
  inactive: 'bg-gray-500/10 text-gray-500',
  on_leave: 'bg-yellow-500/10 text-yellow-500',
  terminated: 'bg-red-500/10 text-red-500',
};

const roleLabels: Record<string, string> = {
  admin: 'Admin',
  project_manager: 'Project Manager',
  developer: 'Developer',
  designer: 'Designer',
  qa: 'QA Engineer',
  content_writer: 'Content Writer',
  client_success: 'Client Success',
};

const statusLabels: Record<string, string> = {
  active: 'Active',
  inactive: 'Inactive',
  on_leave: 'On Leave',
  terminated: 'Terminated',
};

const TeamMemberCard = ({ member }: TeamMemberCardProps) => {
  const initials = member.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Link to={`/team/${member.id}`}>
      <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarImage src={member.avatar_url} alt={member.name} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold text-lg">{member.name}</h3>
                <p className="text-sm text-muted-foreground">{member.email}</p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Role and Status */}
          <div className="flex gap-2">
            <Badge className={roleColors[member.role] || ''}>
              {roleLabels[member.role] || member.role}
            </Badge>
            <Badge className={statusColors[member.status] || ''}>
              {statusLabels[member.status] || member.status}
            </Badge>
          </div>

          {/* Department */}
          {member.department && (
            <div className="text-sm">
              <span className="text-muted-foreground">Department: </span>
              <span className="font-medium">{member.department}</span>
            </div>
          )}

          {/* Hourly Rate */}
          {member.hourly_rate && (
            <div className="text-sm">
              <span className="text-muted-foreground">Rate: </span>
              <span className="font-medium">${member.hourly_rate}/hr</span>
            </div>
          )}

          {/* Stats */}
          {member.stats && (
            <div className="grid grid-cols-2 gap-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Clock className="h-4 w-4 text-blue-500" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Hours</div>
                  <div className="font-semibold">{member.stats.total_hours}h</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <FolderKanban className="h-4 w-4 text-green-500" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Projects</div>
                  <div className="font-semibold">{member.stats.projects_assigned}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <CheckCircle2 className="h-4 w-4 text-purple-500" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Tasks</div>
                  <div className="font-semibold">{member.stats.tasks_completed}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <TrendingUp className="h-4 w-4 text-orange-500" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Utilization</div>
                  <div className="font-semibold">{member.stats.utilization}%</div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

export default TeamMemberCard;
